import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SelectOptionGroup } from '../../../config/types';
import { validateField } from '../../../utils/form-utils';
import ErrorMessage from '../ErrorMessage';
import { DefaultRadio } from './DefaultRadio';
import type { RadioGroupProps, RadioSubGroupProps } from './Props';

const ConditionalRadioSubGroup = ({
  id,
  anchorId,
  currentValue,
  option,
  name,
  disabled,
  handleOnChange,
}: RadioSubGroupProps) => (
  <div className="govuk-radios fieldset-container" data-module="govuk-radios" key={option.label}>
    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
      <h3 className="govuk-fieldset__heading">{option.label}</h3>
    </legend>
    {option.options.map((option, index) => (
      <DefaultRadio
        id={id}
        name={name}
        disabled={disabled}
        key={option}
        anchorId={anchorId}
        index={index}
        item={option as string}
        currentValue={currentValue}
        handleOnChange={handleOnChange}
      />
    ))}
  </div>
);

export default function ConditionalRadioField({
  question,
  anchorId,
  disabled,
  id,
}: RadioGroupProps) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { options, name, title, rules, errorMessages } = question;
  const resolvedOptions = Array.isArray(options) ? options : [];
  const defaultValue = typeof question.defaultValue === 'string' ? question.defaultValue : '';
  const [currentValue, setCurrentValue] = useState(getValues(name) ?? defaultValue);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.value);
    setValue(name, e.target.value);
  };

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });
  const { error } = getFieldState(name);

  const optionIsSubGroup: boolean =
    (resolvedOptions[0] && typeof resolvedOptions[0] === 'object' && 'label' in resolvedOptions[0]) ||
    false;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      <div className="govuk-radios" data-module="govuk-radios">
        {resolvedOptions.map((option, i) => (
          <React.Fragment key={i}>
            {optionIsSubGroup && (
              <ConditionalRadioSubGroup
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                option={option as SelectOptionGroup}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
            {!optionIsSubGroup && (
              <DefaultRadio
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                index={i}
                item={option as string}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
